import type { paths } from '@/services/api/v1.gen';
import { beginReconnecting, endReconnecting } from '@/services/connectionState';
import { observePhotoRequest } from '@/telemetry/photoAvailability';
import { addBreadcrumb } from '@/telemetry/sentry';
import { previewResponseBody, sanitizeUrlForTelemetry } from './errorParser';
import {
  delay,
  drainResponseBody,
  FetchTimeoutError,
  fetchWithRetry,
  waitForOnline,
} from './fetchDriver';

export class HttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly body: unknown,
    public readonly url: string,
    public readonly method: string
  ) {
    super(
      `HTTP ${status} ${statusText} on ${method} ${sanitizeUrlForTelemetry(url)}`
    );
    this.name = 'HttpError';
  }
}

export class NetworkError extends Error {
  constructor(
    message: string,
    public readonly url: string,
    public readonly method: string,
    public readonly cause?: unknown
  ) {
    super(message);
    this.name = 'NetworkError';
  }
}

export class TimeoutError extends Error {
  constructor(
    public readonly url: string,
    public readonly method: string,
    public readonly timeoutMs: number
  ) {
    super(
      `${method} ${sanitizeUrlForTelemetry(url)} timed out after ${timeoutMs}ms`
    );
    this.name = 'TimeoutError';
  }
}

export class HttpParseError extends Error {
  constructor(
    message: string,
    public readonly url: string,
    public readonly method: string,
    public readonly contentType: string,
    public readonly cause?: unknown
  ) {
    super(message);
    this.name = 'HttpParseError';
  }
}

export interface HttpCallOptions {
  timeoutMs?: number;
  maxRetries?: number;
  retryOnTimeout?: boolean;
  maxBackoffMs?: number;
  signal?: AbortSignal;
}

export interface HttpFetchConfig {
  fetchImpl?: typeof fetch;
  headers?: () => Record<string, string>;
}

export type HttpFetch = (
  request: Request,
  options?: HttpCallOptions
) => Promise<Response>;

const DEFAULT_TIMEOUT_MS = 60_000;
const DEFAULT_GET_RETRIES = 3;
const DEFAULT_MUTATION_RETRIES = 1;
const DEFAULT_MAX_BACKOFF_MS = 5_000;
// 408 = stale upstream socket retries are safe; 429 = Stripe rate limit
// for payment intents — silent failure caused real declined-card incidents.
const RETRYABLE_STATUSES = new Set([408, 429, 502, 503, 504]);
const RECONNECTING_STATUSES = new Set([408, 502, 503, 504]);
const SAFE_METHODS = new Set(['GET']);

type EndpointKey = `${'GET' | 'POST'} ${keyof paths}`;

// Exact method + template match: a substring rule handed the payment-intent
// status poll the long budget of its siblings and froze the UI for minutes.
//
// The reader cancel must be longer than the cancel at the backend. The cancel
// of an offline reader takes approximately 7.6 s there. If the kiosk stops to
// wait before the backend answers, the next send meets that cancel on the
// reader, and Stripe refuses the send as terminal_reader_busy.
const ENDPOINT_TIMEOUT_OVERRIDES: Partial<Record<EndpointKey, number>> = {
  'GET /v1/payment-intents/{payment_intent_id}': 10_000,
  'POST /v1/payment-intents/cancel-reader': 15_000,
  'POST /v1/payment-intents': 30_000,
  'POST /v1/payment-intents/{payment_intent_id}/send-to-reader': 30_000,
  'POST /v1/payment-intents/{payment_intent_id}/capture': 30_000,
  'POST /v1/payment-intents/{payment_intent_id}/hold': 15_000,
  'POST /v1/print-jobs': 15_000,
  'GET /v1/print-jobs/{job_key}': 10_000,
  'POST /v1/entr/photo_printed': 20_000,
};

const TIMEOUT_RULES = Object.entries(ENDPOINT_TIMEOUT_OVERRIDES).map(
  ([key, timeoutMs]) => {
    const [method, template] = key.split(' ') as [string, string];
    const pattern = template
      .split(/\{[^}]+\}/)
      .map((part) => part.replace(/[.*+?^$()|[\]\\]/g, '\\$&'))
      .join('[^/]+');
    return { method, pattern: new RegExp(`${pattern}$`), timeoutMs };
  }
);

export function lookupTimeout(method: string, url: string): number {
  const { pathname } = new URL(url, window.location.origin);
  return (
    TIMEOUT_RULES.find(
      (rule) => rule.method === method && rule.pattern.test(pathname)
    )?.timeoutMs ?? DEFAULT_TIMEOUT_MS
  );
}

function fastRetriesEnabled(): boolean {
  try {
    return (
      typeof window.localStorage?.getItem === 'function' &&
      window.localStorage.getItem('entr_dev_fast_http_retries') === 'true'
    );
  } catch {
    return false;
  }
}

function retryDelayMs(attempt: number, maxBackoffMs: number): number {
  if (fastRetriesEnabled()) return 10;
  return Math.min(500 * 2 ** attempt, maxBackoffMs);
}

async function readResponseBody(
  response: Response,
  url: string,
  method: string
): Promise<{ text: string; data: unknown }> {
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    try {
      const text = await response.text();
      return { text, data: JSON.parse(text) };
    } catch (error) {
      drainResponseBody(response);
      throw new HttpParseError(
        `Malformed JSON body on ${method} ${sanitizeUrlForTelemetry(url)}`,
        url,
        method,
        contentType,
        error
      );
    }
  }

  const text = await response.text().catch(() => '');
  return { text, data: text || null };
}

export function createHttpFetch(config: HttpFetchConfig = {}): HttpFetch {
  return async function send(
    request: Request,
    options: HttpCallOptions = {}
  ): Promise<Response> {
    const method = request.method;
    const url = request.url;
    const timeoutMs = options.timeoutMs ?? lookupTimeout(method, url);
    const signal = options.signal ?? request.signal;
    const retryOnTimeout = options.retryOnTimeout ?? true;
    const maxBackoffMs = options.maxBackoffMs ?? DEFAULT_MAX_BACKOFF_MS;
    const maxRetries =
      options.maxRetries ??
      (SAFE_METHODS.has(method)
        ? DEFAULT_GET_RETRIES
        : DEFAULT_MUTATION_RETRIES);

    const headers = new Headers({
      Accept: 'application/json',
      ...config.headers?.(),
    });
    request.headers.forEach((value, key) => {
      headers.set(key, value);
    });
    const body = SAFE_METHODS.has(method)
      ? undefined
      : (await request.text()) || undefined;
    let reconnectingStarted = false;

    const markReconnecting = (): void => {
      if (reconnectingStarted) return;
      reconnectingStarted = true;
      beginReconnecting();
    };

    const markOnline = (): void => {
      if (!reconnectingStarted) return;
      reconnectingStarted = false;
      endReconnecting();
    };

    const photoRequest = observePhotoRequest(request, timeoutMs);
    let outcome: 'completed' | 'failed' | 'aborted' = 'failed';
    try {
      const response = await fetchWithRetry({
        url,
        init: { method, headers, body },
        signal,
        timeoutMs,
        fetchImpl: config.fetchImpl,
        maxRetries,
        shouldRetryResponse: (response) =>
          RETRYABLE_STATUSES.has(response.status),
        shouldRetryError: (error) =>
          retryOnTimeout || !(error instanceof FetchTimeoutError),
        beforeRetry: async ({ response, error, attempt, maxRetries }) => {
          photoRequest?.retry(attempt);
          if (response) {
            if (RECONNECTING_STATUSES.has(response.status)) {
              markReconnecting();
            }
            addBreadcrumb('http.retry', 'Retrying transient HTTP error', {
              url: sanitizeUrlForTelemetry(url),
              method,
              attempt,
              max: maxRetries,
              status: response.status,
            });
          } else {
            markReconnecting();
            addBreadcrumb('http.retry', 'Retrying network failure', {
              url: sanitizeUrlForTelemetry(url),
              method,
              attempt,
              max: maxRetries,
              kind: error instanceof FetchTimeoutError ? 'timeout' : 'network',
            });
            await waitForOnline(8_000, signal);
          }
          await delay(retryDelayMs(attempt - 1, maxBackoffMs), signal);
        },
      });
      markOnline();
      photoRequest?.receivedHeaders(response.status);

      if (!response.ok) {
        let errorBody: unknown = null;
        try {
          errorBody = (await readResponseBody(response, url, method)).data;
        } catch (parseError) {
          if (!(parseError instanceof HttpParseError)) {
            throw parseError;
          }
        }
        throw new HttpError(
          response.status,
          response.statusText,
          errorBody,
          url,
          method
        );
      }

      // Read and validated here so a malformed body surfaces as
      // HttpParseError; openapi-fetch parses the re-wrapped text.
      const { text } = await readResponseBody(response, url, method);
      const result = new Response(text || null, {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      });
      outcome = 'completed';
      return result;
    } catch (error) {
      markOnline();
      if (error instanceof HttpError) throw error;
      if (error instanceof HttpParseError) throw error;
      if (signal?.aborted) {
        outcome = 'aborted';
        throw error;
      }
      if (error instanceof FetchTimeoutError) {
        throw new TimeoutError(url, method, timeoutMs);
      }
      throw new NetworkError(
        error instanceof Error ? error.message : 'Network error',
        url,
        method,
        error
      );
    } finally {
      photoRequest?.finish(outcome);
    }
  };
}

export function getHttpErrorTelemetry(error: unknown): Record<string, unknown> {
  if (error instanceof HttpError) {
    return {
      kind: 'http',
      statusCode: error.status,
      statusText: error.statusText,
      method: error.method,
      url: sanitizeUrlForTelemetry(error.url),
      responsePreview: previewResponseBody(error.body),
    };
  }
  if (error instanceof TimeoutError) {
    return {
      kind: 'timeout',
      method: error.method,
      url: sanitizeUrlForTelemetry(error.url),
      timeoutMs: error.timeoutMs,
    };
  }
  if (error instanceof NetworkError) {
    return {
      kind: 'network',
      method: error.method,
      url: sanitizeUrlForTelemetry(error.url),
    };
  }
  if (error instanceof HttpParseError) {
    return {
      kind: 'parse',
      method: error.method,
      url: sanitizeUrlForTelemetry(error.url),
      contentType: error.contentType,
    };
  }
  return { kind: 'unknown' };
}

export const HTTP_DEFAULTS = {
  DEFAULT_TIMEOUT_MS,
  DEFAULT_GET_RETRIES,
  DEFAULT_MUTATION_RETRIES,
  RETRYABLE_STATUSES: [...RETRYABLE_STATUSES],
  ENDPOINT_TIMEOUT_OVERRIDES,
} as const;
